import React, { useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { logout } from '../../services/auth';
import theme from '../../theme';
import { isRegistrationEnabled } from '../../config/featureFlags';

function Header({ sections = {} }) {
  const { user, setUser } = useContext(AuthContext);
  const [isLoggedIn, setIsLoggedIn] = useState(!!user);
  const [mobileOpen, setMobileOpen] = useState(false);
  const history = useHistory();

  useEffect(() => {
    setIsLoggedIn(!!user);
    const isAuthErrorLogout = localStorage.getItem('authErrorLogout') === 'true';
    const redirectAfterAuthPaths = new Set(['/', '/login', '/register']);

    if (user && !isAuthErrorLogout && redirectAfterAuthPaths.has(history.location.pathname)) {
      history.push('/dashboard');
    }
  }, [user, history]);

  const handleLogout = () => {
    logout();
    setUser(null);
    history.push('/login');
  };

  const navLinkClass = `px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 ${theme.navLink} cursor-pointer`;

  return (
    <header
      className={`sticky top-0 z-50 ${theme.headerBg}`}
      style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}
    >
      <div className="container mx-auto px-4 py-3 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <div className="flex flex-col">
            <h1 className={`text-lg sm:text-xl font-bold tracking-wide ${theme.textPrimary} group-hover:text-olive-600 transition-colors`}>
              MOZART TRADER
            </h1>
            <p className={`text-[10px] sm:text-xs tracking-widest uppercase ${theme.textMuted}`}>
              trade your symphony
            </p>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:block">
          <ul className="flex items-center gap-1">
            {user ? (
              <>
                <li><Link to="/dashboard" className={navLinkClass}>Dashboard</Link></li>
                <li><Link to="/favorites" className={navLinkClass}>Favorites</Link></li>
                <li><div onClick={() => window.location = `${process.env.REACT_APP_API_BASE_URL.replace('api', '')}/chartplotter`} className={navLinkClass}>Chartplotter</div></li>
                <li><Link to="/profile" className={navLinkClass}>Profile</Link></li>
                <li><Link to="/config" className={navLinkClass}>Config</Link></li>
                <li><Link to="/tornado" className={navLinkClass}>Tornado</Link></li>
                <li><Link to="/achievements" className={navLinkClass}>Achievements</Link></li>
                <li><Link to="/forecasts" className={navLinkClass}>Forecasts</Link></li>
                <li><Link to="/health" className={navLinkClass}>Health</Link></li>
                <li><Link to="/admin" className={navLinkClass}>Admin</Link></li>
                <li>
                  <button
                    onClick={handleLogout}
                    className="px-3 py-1.5 text-sm font-medium text-red-500 rounded-lg transition-all duration-200 hover:bg-red-50 hover:text-red-600"
                  >
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <>
                <li><Link to="/tornado" className={navLinkClass}>Tornado</Link></li>
                {sections.achievements === true && <li><Link to="/achievements" className={navLinkClass}>Achievements</Link></li>}
                {sections.forecasts === true && <li><Link to="/forecasts" className={navLinkClass}>Forecasts</Link></li>}
                <li><Link to="/admin" className={navLinkClass}>Admin</Link></li>
                <li><Link to="/login" className={navLinkClass}>Login</Link></li>
                {isRegistrationEnabled() && (
                <li>
                  <Link
                    to="/register"
                    className={`px-4 py-1.5 text-sm font-semibold rounded-lg transition-all duration-200 hover:shadow-md ${theme.btnPrimary}`}
                  >
                    Register
                  </Link>
                </li>
                )}
              </>
            )}
          </ul>
        </nav>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden p-2 rounded-lg transition-colors ${theme.textSecondary} hover:bg-olive-50`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="md:hidden border-t border-olive-100 bg-white px-4 py-2 space-y-1">
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Dashboard</Link>
              <Link to="/favorites" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Favorites</Link>
              <div onClick={() => { setMobileOpen(false); window.location = `${process.env.REACT_APP_API_BASE_URL.replace('api', '')}/chartplotter` }} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Chartplotter</div>
              <Link to="/profile" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Profile</Link>
              <Link to="/config" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Config</Link>
              <Link to="/tornado" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Tornado</Link>
              <Link to="/achievements" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Achievements</Link>
              <Link to="/forecasts" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Forecasts</Link>
              <Link to="/health" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Health</Link>
              <Link to="/admin" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Admin</Link>
              <button onClick={handleLogout} className="block w-full text-left px-3 py-2 text-sm text-red-500 rounded-lg hover:bg-red-50">Logout</button>
            </>
          ) : (
            <>
              <Link to="/tornado" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Tornado</Link>
              {sections.achievements === true && <Link to="/achievements" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Achievements</Link>}
              {sections.forecasts === true && <Link to="/forecasts" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Forecasts</Link>}
              <Link to="/admin" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Admin</Link>
              <Link to="/login" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm rounded-lg ${theme.navLink}`}>Login</Link>
              {isRegistrationEnabled() && (
                <Link to="/register" onClick={() => setMobileOpen(false)} className={`block px-3 py-2 text-sm font-semibold rounded-lg text-olive-700 hover:bg-olive-50`}>Register</Link>
              )}
            </>
          )}
        </div>
      )}
    </header>
  );
}

export default Header;
